import { Amplify } from 'aws-amplify';
import { signIn, signUp, confirmSignUp, signOut, resetPassword, confirmResetPassword, getCurrentUser, fetchAuthSession } from 'aws-amplify/auth';
import config from './config';

// Configure Amplify — only if Cognito is configured
if (config.cognito.userPoolId) {
  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId: config.cognito.userPoolId,
        userPoolClientId: config.cognito.userPoolClientId,
        signUpVerificationMethod: 'code',
      },
    },
  });
}

export async function authSignUp(email, password, givenName, familyName) {
  const result = await signUp({
    username: email,
    password,
    options: {
      userAttributes: {
        email,
        ...(givenName ? { given_name: givenName } : {}),
        ...(familyName ? { family_name: familyName } : {}),
      },
    },
  });
  return result;
}

export async function authConfirmSignUp(email, code) {
  return confirmSignUp({ username: email, confirmationCode: code });
}

export async function authSignIn(email, password) {
  return signIn({ username: email, password });
}

export async function authSignOut() {
  return signOut();
}

export async function authResetPassword(email) {
  return resetPassword({ username: email });
}

export async function authConfirmResetPassword(email, code, newPassword) {
  return confirmResetPassword({ username: email, confirmationCode: code, newPassword });
}

export async function authGetCurrentUser() {
  try {
    return await getCurrentUser();
  } catch {
    return null;
  }
}

export async function authGetToken() {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() || null;
  } catch {
    return null;
  }
}

export function isAuthConfigured() {
  return Boolean(config.cognito.userPoolId);
}

/**
 * SSO bootstrap — called on page load when a ?token=<idToken> param is present.
 *
 * The other Dream and Build Cloud app passes a Cognito ID token in the URL after
 * the user is already signed in there. We decode the JWT locally (no network call
 * needed — we just need the claims), seed Amplify's token store so it believes the
 * user is signed in, then strip the token from the URL.
 *
 * Returns the Amplify user object on success, or null if the token is missing /
 * invalid / from the wrong pool.
 */
export async function authBootstrapFromUrlToken() {
  const params = new URLSearchParams(window.location.search);
  const idToken = params.get('token');
  const accessToken = params.get('access_token');
  if (!idToken) return null;

  // Always strip the tokens from the URL immediately — even if validation
  // fails — so they are never left sitting in browser history.
  params.delete('token');
  params.delete('access_token');
  const remaining = params.toString();
  const cleanUrl = window.location.pathname + (remaining ? '?' + remaining : '');
  window.history.replaceState({}, '', cleanUrl);

  try {
    // Decode the JWT payload (base64url, middle segment)
    const payloadB64 = idToken.split('.')[1];
    if (!payloadB64) throw new Error('Malformed JWT');
    const payload = JSON.parse(atob(payloadB64.replace(/-/g, '+').replace(/_/g, '/')));

    // Validate issuer matches our pool — reject tokens from other pools
    const expectedIss = `https://cognito-idp.${config.cognito.region}.amazonaws.com/${config.cognito.userPoolId}`;
    if (payload.iss !== expectedIss) {
      console.warn('[SSO] Token issuer mismatch — ignoring token');
      return null;
    }

    // Validate audience matches our app client
    const aud = payload.aud || payload.client_id;
    if (aud !== config.cognito.userPoolClientId) {
      console.warn('[SSO] Token audience mismatch — ignoring token');
      return null;
    }

    // Check expiry
    if (payload.exp && payload.exp * 1000 < Date.now()) {
      console.warn('[SSO] Token is expired — ignoring token');
      return null;
    }

    // Seed Amplify's Cognito token store so getCurrentUser() works without a
    // full sign-in round-trip. Amplify v6 stores tokens keyed by:
    //   CognitoIdentityServiceProvider.<clientId>.<username>.<tokenType>
    const clientId = config.cognito.userPoolClientId;
    const username = payload['cognito:username'] || payload.sub;
    const prefix = `CognitoIdentityServiceProvider.${clientId}`;

    localStorage.setItem(`${prefix}.LastAuthUser`, username);
    localStorage.setItem(`${prefix}.${username}.idToken`, idToken);
    // Seed the REAL access token when the handoff provided one. Falling back
    // to the id token makes Amplify v6 reject the session (token_use
    // mismatch) — which is why SSO previously dropped to the login screen.
    localStorage.setItem(`${prefix}.${username}.accessToken`, accessToken || idToken);
    // clockDrift must be present or Amplify v6 treats the token store as
    // incomplete; 0 is correct since these tokens were just minted.
    localStorage.setItem(`${prefix}.${username}.clockDrift`, '0');

    // Now ask Amplify to read the session it just found in localStorage
    const user = await getCurrentUser();

    // Stamp the email claim onto signInDetails.loginId so downstream code
    // (e.g. ADMIN_EMAILS check in App.js) finds it. Amplify password-login
    // sets signInDetails.loginId to the email the user typed, but the SSO
    // bootstrap path does not populate it because we never call signIn().
    // Without this, isAdmin returns false for SSO-authenticated users.
    if (user && payload.email) {
      user.signInDetails = user.signInDetails || {};
      user.signInDetails.loginId = payload.email;
    }
    console.log('[SSO] Bootstrapped session for', user?.signInDetails?.loginId || user?.username || username);
    return user;
  } catch (err) {
    console.error('[SSO] Failed to bootstrap from URL token:', err);
    return null;
  }
}

/**
 * Best-effort email extraction from a `?token=<idToken>` URL param.
 *
 * Used to pre-fill the AuthScreen email field when an SSO handoff from
 * the Learning Platform is in flight but `authBootstrapFromUrlToken()`
 * rejected the token (e.g. the issuer is the Learning Platform's pool
 * instead of the exam-app's pool, or the token is expired).
 *
 * This function does NOT validate the token — it just decodes the
 * payload locally to read the email claim. Safe because the email is
 * only used to pre-populate a form field; nothing is granted on the
 * basis of an unverified email.
 *
 * Returns the lowercased email on success, or null on any failure.
 *
 * Note: this does NOT strip the token from the URL. The caller of
 * `authBootstrapFromUrlToken()` already does that, and we typically
 * call this AFTER the bootstrap returns null on the same page load.
 */
export function extractEmailFromUrlToken() {
  try {
    const params = new URLSearchParams(window.location.search);
    const idToken = params.get('token');
    if (!idToken) return null;
    const payloadB64 = idToken.split('.')[1];
    if (!payloadB64) return null;
    const payload = JSON.parse(atob(payloadB64.replace(/-/g, '+').replace(/_/g, '/')));
    const email = (payload?.email || '').trim().toLowerCase();
    return email || null;
  } catch {
    return null;
  }
}
